// AUTO-GENERATED FILE - DO NOT EDIT
// Generated by scripts/generate-cube-meta.ts
import type {
  CubeMeasureEntry,
  CubeDimensionEntry,
  CubeJoinEntry
} from 'shared/src/api/cube-api-types';

export const platformEntitiesCubeMeta = {
  name: 'platform_entities',
  sqlTable: 'metrics.platform_entities',
  title: 'Platform Entities',
  description: 'Platform entity dimension. One row per entity from media_platform_entities.',
  joins: {
    platform_entity_changes_daily: { relationship: 'one_to_many' } as const satisfies CubeJoinEntry,
    campaigns: { relationship: 'many_to_one' } as const satisfies CubeJoinEntry
  },
  measures: {
    'platform_entities.count': {
      type: 'number',
      aggType: 'count',
      title: 'Entity Count',
      description: 'Number of platform entities.',
      meta: {
        category: 'utility',
        grain: 'entity',
        supports_rollup: true,
        formula: 'COUNT(*)',
        inputs: [],
        short_description: 'Number of platform entities',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeMeasureEntry,
    'platform_entities.total_change_days': {
      sql: 'total_change_days',
      type: 'number',
      aggType: 'sum',
      title: 'Total Change Days',
      description:
        'Sum of distinct days with manual updates across entities (excludes creation events). NOTE: This is NOT "total distinct days with any change across all entities" — it sums per-entity distinct day counts, so overlapping days are counted multiple times.',
      meta: {
        category: 'changelog',
        grain: 'entity',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_change_days)',
        inputs: [],
        short_description: 'Sum of per-entity distinct days with manual updates only',
        source_tables: ['metrics.platform_entities'],
        rolled_up_from: 'platform_entity_changes_daily.change_count'
      }
    } as const satisfies CubeMeasureEntry,
    'platform_entities.total_field_changes': {
      sql: 'total_field_changes',
      type: 'number',
      aggType: 'sum',
      title: 'Total Field Changes',
      description:
        'Sum of manual update field change counts across entities (excludes creation events).',
      meta: {
        category: 'changelog',
        grain: 'entity',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_field_changes)',
        inputs: [],
        short_description: 'Sum of manual update field change counts only',
        source_tables: ['metrics.platform_entities'],
        rolled_up_from: 'platform_entity_changes_daily.change_count'
      }
    } as const satisfies CubeMeasureEntry
  },
  dimensions: {
    'platform_entities.platform_entity_id': {
      sql: 'platform_entity_id',
      type: 'string',
      title: 'Platform Entity Id',
      primaryKey: true,
      meta: {
        short_description: 'Unique platform entity identifier (uuid)',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.entity_type': {
      sql: 'entity_type',
      type: 'string',
      title: 'Entity Type',
      meta: {
        short_description: 'Platform-specific entity type (e.g., line_item, ad_group, campaign)',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.entity_name': {
      sql: 'entity_name',
      type: 'string',
      title: 'Entity Name',
      meta: {
        short_description: 'Display name of the platform entity',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.native_entity_id': {
      sql: 'native_entity_id',
      type: 'string',
      title: 'Native Platform Entity ID',
      description:
        "The entity ID as it exists on the originating DSP (e.g. Google Ads campaign number, Trade Desk ID, Meta ad set ID). Distinct from platform_entity_id, which is Bravo's internal UUID.",
      meta: {
        short_description: 'Native ID on the originating DSP',
        source_tables: ['metrics.platform_entities', 'media_tool.media_platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.media_platform_id': {
      sql: 'media_platform_id',
      type: 'string',
      title: 'Platform ID',
      meta: {
        short_description: 'Media platform foreign key',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.platform_name': {
      sql: 'platform_name',
      type: 'string',
      title: 'Platform',
      meta: {
        short_description: 'Platform display name (e.g., Beeswax, Trade Desk, Facebook)',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.bravo_entity_type': {
      sql: 'bravo_entity_type',
      type: 'string',
      title: 'Bravo Entity Type',
      description:
        "Bravo concept this entity maps to. 'media_buy' if linked via media_buys, NULL otherwise.",
      meta: {
        short_description: "Bravo mapping: 'media_buy' if linked, NULL otherwise",
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.is_deleted': {
      sql: 'is_deleted',
      type: 'boolean',
      title: 'Is Deleted',
      meta: {
        short_description: 'Whether the entity has been soft-deleted from the platform',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.media_buy_id': {
      sql: 'media_buy_id',
      type: 'string',
      title: 'Media Buy ID',
      meta: {
        short_description: 'Linked media buy (1:1 relationship)',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.line_item_id': {
      sql: 'line_item_id',
      type: 'string',
      title: 'Line Item ID',
      meta: {
        short_description: 'Linked line item (via media plan)',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.campaign_id': {
      sql: 'campaign_id',
      type: 'string',
      title: 'Campaign ID',
      meta: {
        short_description: 'Linked campaign (via strategy)',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.account_id': {
      sql: 'account_id',
      type: 'string',
      title: 'Account ID',
      meta: {
        short_description: 'Linked account',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.total_change_days_value': {
      sql: 'total_change_days',
      type: 'number',
      title: 'Total Change Days',
      description:
        'Pre-aggregated: distinct days with manual updates (excludes creation events and automated changes)',
      meta: {
        filter_only: true,
        measure_counterpart: 'platform_entities.total_change_days',
        short_description: 'Distinct days with manual updates only',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.total_field_changes_value': {
      sql: 'total_field_changes',
      type: 'number',
      title: 'Total Field Changes',
      description:
        'Pre-aggregated: total manual update field changes (excludes creation events and automated changes)',
      meta: {
        filter_only: true,
        measure_counterpart: 'platform_entities.total_field_changes',
        short_description: 'Total manual update field changes only',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.first_change_date': {
      sql: 'first_change_date',
      type: 'time',
      title: 'First Change Date',
      description: 'Earliest day with a manual update (excludes creation events)',
      meta: {
        short_description: 'First manual update date',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.last_change_date': {
      sql: 'last_change_date',
      type: 'time',
      title: 'Last Change Date',
      description: 'Most recent day with a manual update (excludes creation events)',
      meta: {
        short_description: 'Last manual update date',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entities.first_seen_date': {
      sql: 'first_seen_date',
      type: 'time',
      title: 'First Seen Date',
      description:
        'Date when the entity was first observed in the changelog (within 90-day retention window; NULL if creation predates window)',
      meta: {
        short_description: 'Earliest creation event date (may be NULL for old entities)',
        source_tables: ['metrics.platform_entities']
      }
    } as const satisfies CubeDimensionEntry
  }
};
